import { toCsv } from '@/lib/csv';
import { downloadTextFile } from '@/lib/downloadTextFile';

// A homework question to export a column-group for. Built from the normalized
// component questions (only assigned questions are exported). `index` is the
// question's position within the section's question_stats, used to line each
// student's raw stats up with the right question.
export interface DetailedCsvQuestion {
  correctAnswer?: number | string;
  // Scientific-notation exponent for a large fixed numerical answer (the backend
  // stores magnitudes >1e9 as mantissa + extra exponent; see proto Answer).
  correctAnswerExtraExponent?: number;
  correctAnswerIndex?: number;
  // Every correct option, for select-all. correctAnswerIndex above cannot carry
  // more than one, so a select-all export would silently show only the first.
  correctAnswerIndices?: number[];
  index: number;
  isAiUpload: boolean;
  kind:
    | 'aiUpload'
    | 'exactAnswer'
    | 'multiPart'
    | 'multipleChoice'
    | 'numerical'
    | 'selectAll';
  // "Q1", "Q2", ... numbered across the assigned questions.
  label: string;
  maxScore: number;
  parts?: DetailedCsvQuestion[];
  // Whether the question resolves per-student randomized variables. Drives the
  // extra "Variables" column (see showsVariablesColumn).
  usesRandomVariables?: boolean;
}

// A raw per-question stat as returned by GetCourseStudents (both snake_case and
// camelCase spellings tolerated). Not every field is declared on the app's
// RawQuestionStats, so this is a purpose-built, permissive shape.
export interface RawDetailedQuestionStat {
  answer?: RawDetailedAnswer | null;
  current_num_ai_hints?: number | string | null;
  current_num_ai_practice_problems?: number | string | null;
  current_num_attempts?: number | string | null;
  currentNumAiHints?: number | string | null;
  currentNumAiPracticeProblems?: number | string | null;
  currentNumAttempts?: number | string | null;
  grading_details?: RawDetailedGradingDetails | null;
  gradingDetails?: RawDetailedGradingDetails | null;
  needs_grading?: boolean | null;
  needsGrading?: boolean | null;
  question_set_stats?: RawDetailedQuestionSetStats | null;
  questionSetStats?: RawDetailedQuestionSetStats | null;
  score?: number | string | null;
}

interface RawDetailedQuestionSetStats {
  question_stats?: RawDetailedQuestionStat[] | null;
  questionStats?: RawDetailedQuestionStat[] | null;
}

interface RawDetailedAnswer {
  answer_index?: Array<number | string> | null;
  answerIndex?: Array<number | string> | null;
  // The student-specific resolved correct answer for a randomized question. The
  // component definition has no concrete answer for these, so the solution MUST
  // come from the student's own stat (proto Answer.current_random_*).
  current_random_exact_answer?: string | null;
  currentRandomExactAnswer?: string | null;
  current_random_numerical_answer?: number | string | null;
  currentRandomNumericalAnswer?: number | string | null;
  current_random_numerical_answer_extra_exponent?: number | string | null;
  currentRandomNumericalAnswerExtraExponent?: number | string | null;
  exact_answer?: string | null;
  exactAnswer?: string | null;
  file_upload_url?: string | null;
  fileUploadUrl?: string | null;
  numerical_answer?: number | string | null;
  numericalAnswer?: number | string | null;
  numerical_answer_extra_exponent?: number | string | null;
  numericalAnswerExtraExponent?: number | string | null;
  // The student's resolved random-variable values (proto Answer.variables),
  // persisted with their answer — the exact numbers they saw for a randomized
  // question. Absent on legacy answers saved before variable persistence.
  variables?: RawDetailedVariable[] | null;
}

// One resolved random variable on a student's answer. `current_value` is the
// numeric magnitude the student saw; `current_content_pair_index` is the
// selected option for a content-based variable.
interface RawDetailedVariable {
  current_content_pair_index?: number | string | null;
  currentContentPairIndex?: number | string | null;
  current_value?: number | string | null;
  currentValue?: number | string | null;
  name?: string | null;
}

interface RawDetailedGradingDetails {
  feedback?: string | null;
  manual_feedback?: string | null;
  manualFeedback?: string | null;
}

export interface DetailedCsvStudent {
  email: string;
  firstName: string;
  instituteId: string;
  lastName: string;
  // The student's question_stats for the exported component's first section, in
  // question order.
  questionStats: RawDetailedQuestionStat[];
}

const STUDENT_HEADERS = ['Last Name', 'First Name', 'Email', 'ID', 'Total Score'];

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(4)));
}

// Render a numerical value that may carry a scientific-notation exponent (the
// backend splits magnitudes >1e9 into a mantissa + extra exponent, so dropping
// the exponent would understate the answer by orders of magnitude). Matches the
// app's own `<mantissa>e<exponent>` rendering (see randomVariables formatting).
function formatNumericalWithExponent(value: number, extraExponent?: number): string {
  const base = formatNumber(value);
  if (extraExponent === undefined || extraExponent === 0) {
    return base;
  }
  return `${base}e${extraExponent}`;
}

// A -> Z for a 0-based multiple-choice index.
function multipleChoiceLetter(index: number): string {
  if (index < 0) {
    return '';
  }
  return String.fromCharCode(65 + index);
}

// Select-all options as multiple choice's letters, in option order: "A, C".
function optionLetters(indexes: ReadonlyArray<number | string> | undefined): string {
  return (indexes ?? [])
    .map(toNumber)
    .filter((index): index is number => index !== undefined && index >= 0)
    .sort((a, b) => a - b)
    .map(multipleChoiceLetter)
    .join(', ');
}

function nestedStats(stat: RawDetailedQuestionStat | undefined): RawDetailedQuestionStat[] {
  const set = stat?.question_set_stats ?? stat?.questionSetStats;
  const list = set?.question_stats ?? set?.questionStats;
  return Array.isArray(list) ? list : [];
}

function getAnswerField<T>(
  answer: RawDetailedAnswer | null | undefined,
  snake: keyof RawDetailedAnswer,
  camel: keyof RawDetailedAnswer,
): T | undefined {
  if (!answer) {
    return undefined;
  }
  return (answer[snake] ?? answer[camel]) as T | undefined;
}

// The student-specific resolved correct answer for a randomized numerical
// question (mantissa + optional extra exponent), or undefined when the question
// is not randomized.
function resolvedRandomNumerical(
  answer: RawDetailedAnswer | null | undefined,
): { exponent?: number; value: number } | undefined {
  const value = toNumber(
    getAnswerField(answer, 'current_random_numerical_answer', 'currentRandomNumericalAnswer'),
  );
  if (value === undefined) {
    return undefined;
  }
  return {
    exponent: toNumber(
      getAnswerField(
        answer,
        'current_random_numerical_answer_extra_exponent',
        'currentRandomNumericalAnswerExtraExponent',
      ),
    ),
    value,
  };
}

// The student-specific resolved correct answer for a randomized exact-answer
// question, or undefined when the question is not randomized.
function resolvedRandomExact(
  answer: RawDetailedAnswer | null | undefined,
): string | undefined {
  const value = getAnswerField<string>(
    answer,
    'current_random_exact_answer',
    'currentRandomExactAnswer',
  );
  return typeof value === 'string' && value !== '' ? value : undefined;
}

export function formatStudentAnswer(
  stat: RawDetailedQuestionStat | undefined,
  question: DetailedCsvQuestion,
): string {
  if (question.kind === 'multiPart') {
    const parts = question.parts ?? [];
    const nested = nestedStats(stat);
    return parts
      .map((part, partIndex) => formatStudentAnswer(nested[partIndex], part))
      .join(' | ');
  }

  const answer = stat?.answer ?? null;

  if (question.kind === 'multipleChoice') {
    const indexes = getAnswerField<Array<number | string>>(
      answer,
      'answer_index',
      'answerIndex',
    );
    const selected = toNumber(indexes?.[0]);
    return selected === undefined ? '' : multipleChoiceLetter(selected);
  }

  if (question.kind === 'selectAll') {
    return optionLetters(
      getAnswerField<Array<number | string>>(answer, 'answer_index', 'answerIndex'),
    );
  }

  if (question.kind === 'numerical') {
    const value = toNumber(
      getAnswerField(answer, 'numerical_answer', 'numericalAnswer'),
    );
    if (value === undefined) {
      return '';
    }
    const exponent = toNumber(
      getAnswerField(
        answer,
        'numerical_answer_extra_exponent',
        'numericalAnswerExtraExponent',
      ),
    );
    return formatNumericalWithExponent(value, exponent);
  }

  if (question.kind === 'exactAnswer') {
    return getAnswerField<string>(answer, 'exact_answer', 'exactAnswer') ?? '';
  }

  if (question.kind === 'aiUpload') {
    return getAnswerField<string>(answer, 'file_upload_url', 'fileUploadUrl') ?? '';
  }

  return '';
}

// The correct answer for one question. For randomized questions the answer is
// student-specific and lives ONLY in the student's stat (the component
// definition has no concrete answer), so the stat is consulted first and the
// component-level answer is the non-randomized fallback.
/*
 * A randomized question has no single solution, so the only valid answer for a
 * row is the one resolved for THAT student and stored on their stat. The
 * question-level correctAnswer is the instructor's representative answer — the
 * one shown beside the prompt in the preview — and printing it against a student
 * who was never served those numbers would assert something untrue about them.
 * Blank instead, as it was before the question-level answer existed for
 * randomized questions at all.
 */
export function formatSolution(
  stat: RawDetailedQuestionStat | undefined,
  question: DetailedCsvQuestion,
): string {
  if (question.kind === 'multiPart') {
    const parts = question.parts ?? [];
    const nested = nestedStats(stat);
    return parts
      .map((part, partIndex) => formatSolution(nested[partIndex], part))
      .join(' | ');
  }
  if (question.kind === 'multipleChoice') {
    return question.correctAnswerIndex === undefined
      ? ''
      : multipleChoiceLetter(question.correctAnswerIndex);
  }
  if (question.kind === 'selectAll') {
    return optionLetters(question.correctAnswerIndices);
  }

  const answer = stat?.answer ?? null;

  if (question.kind === 'numerical') {
    const random = resolvedRandomNumerical(answer);
    if (random !== undefined) {
      return formatNumericalWithExponent(random.value, random.exponent);
    }
    return question.correctAnswer === undefined || question.usesRandomVariables
      ? ''
      : formatNumericalWithExponent(
          Number(question.correctAnswer),
          question.correctAnswerExtraExponent,
        );
  }
  if (question.kind === 'exactAnswer') {
    const random = resolvedRandomExact(answer);
    if (random !== undefined) {
      return random;
    }
    return question.correctAnswer === undefined || question.usesRandomVariables
      ? ''
      : String(question.correctAnswer);
  }
  return '';
}

// Whether a question gets the extra per-student "Variables" column: only
// randomized numerical or multi-part questions, i.e. the kinds whose resolved
// variable values are actually persisted. Randomized MC / exact questions do NOT
// persist variables, so they would only add an always-empty column.
function showsVariablesColumn(question: DetailedCsvQuestion): boolean {
  return (
    Boolean(question.usesRandomVariables) &&
    (question.kind === 'numerical' || question.kind === 'multiPart')
  );
}

function answerVariables(
  stat: RawDetailedQuestionStat | undefined,
): RawDetailedVariable[] {
  const list = stat?.answer?.variables;
  return Array.isArray(list) ? list : [];
}

// One variable's displayed value — the number the student saw. Numeric variables
// use current_value; content-based variables fall back to their selected option
// index. Formatted like the export's other numbers (see formatNumber).
function formatVariableValue(variable: RawDetailedVariable): string {
  const value = toNumber(variable.current_value ?? variable.currentValue);
  if (value !== undefined) {
    return formatNumber(value);
  }
  const contentIndex = toNumber(
    variable.current_content_pair_index ?? variable.currentContentPairIndex,
  );
  return contentIndex === undefined ? '' : String(contentIndex);
}

// A student's variable values as one CSV-safe cell: "name=value : name=value".
// The " : " separator avoids the comma that would force CSV quoting. Empty when
// no named variables are recorded.
function formatVariables(variables: RawDetailedVariable[]): string {
  return variables
    .map((variable) => {
      const name = typeof variable.name === 'string' ? variable.name.trim() : '';
      return name === '' ? '' : `${name}=${formatVariableValue(variable)}`;
    })
    .filter((entry) => entry !== '')
    .join(' : ');
}

// The Variables cell for one student on one question. Multi-part parts share a
// single per-student variable set (seeded once), so it is shown ONCE — from the
// parent, else the first part that recorded values — rather than repeated per
// part. Empty when the question is not a variables column or nothing is recorded.
export function formatVariablesCell(
  stat: RawDetailedQuestionStat | undefined,
  question: DetailedCsvQuestion,
): string {
  if (!showsVariablesColumn(question)) {
    return '';
  }
  if (question.kind === 'multiPart') {
    const own = formatVariables(answerVariables(stat));
    if (own !== '') {
      return own;
    }
    for (const partStat of nestedStats(stat)) {
      const formatted = formatVariables(answerVariables(partStat));
      if (formatted !== '') {
        return formatted;
      }
    }
    return '';
  }
  return formatVariables(answerVariables(stat));
}

// Column headers for one question, in the order the value cells are produced. A
// "Variables" column follows Solution for randomized numerical / multi-part
// questions. The AI Feedback column is only present for AI-upload questions (the
// only kind that carries model feedback).
export function questionColumnHeaders(question: DetailedCsvQuestion): string[] {
  const headers = [
    `${question.label} Score`,
    `${question.label} Answer`,
    `${question.label} Solution`,
  ];
  if (showsVariablesColumn(question)) {
    headers.push(`${question.label} Variables`);
  }
  headers.push(
    `${question.label} Attempts`,
    `${question.label} AI Hints`,
    `${question.label} AI Practice`,
    `${question.label} Needs Grading`,
  );
  if (question.isAiUpload) {
    headers.push(`${question.label} AI Feedback`);
  }
  return headers;
}

// The Max-Score row cells for one question: the max in the Score position, blanks
// elsewhere — kept the same length as the header group so columns stay aligned.
export function questionMaxCells(question: DetailedCsvQuestion): string[] {
  // Score, Answer, Solution.
  const cells = [formatNumber(question.maxScore), '', ''];
  if (showsVariablesColumn(question)) {
    cells.push('');
  }
  // Attempts, AI Hints, AI Practice, Needs Grading.
  cells.push('', '', '', '');
  if (question.isAiUpload) {
    cells.push('');
  }
  return cells;
}

function statNumberCell(...values: unknown[]): string {
  for (const value of values) {
    const parsed = toNumber(value);
    if (parsed !== undefined) {
      return formatNumber(parsed);
    }
  }
  return '';
}

// The value cells for one student on one question, aligned with questionColumnHeaders.
export function questionStudentCells(
  stat: RawDetailedQuestionStat | undefined,
  question: DetailedCsvQuestion,
): string[] {
  const grading = stat?.grading_details ?? stat?.gradingDetails ?? null;
  const feedback = grading?.feedback ?? grading?.manual_feedback ?? grading?.manualFeedback ?? '';
  const needsGrading = stat?.needs_grading ?? stat?.needsGrading;

  const cells = [
    statNumberCell(stat?.score) || '0',
    formatStudentAnswer(stat, question),
    formatSolution(stat, question),
  ];
  if (showsVariablesColumn(question)) {
    cells.push(formatVariablesCell(stat, question));
  }
  cells.push(
    statNumberCell(stat?.current_num_attempts, stat?.currentNumAttempts),
    statNumberCell(stat?.current_num_ai_hints, stat?.currentNumAiHints),
    statNumberCell(
      stat?.current_num_ai_practice_problems,
      stat?.currentNumAiPracticeProblems,
    ),
    needsGrading ? 'Yes' : '',
  );
  if (question.isAiUpload) {
    cells.push(feedback ?? '');
  }
  return cells;
}

function studentTotalScore(
  student: DetailedCsvStudent,
  questions: DetailedCsvQuestion[],
): number {
  return questions.reduce((total, question) => {
    const stat = student.questionStats[question.index];
    return total + (toNumber(stat?.score) ?? 0);
  }, 0);
}

export function buildDetailedScoresRows(
  questions: DetailedCsvQuestion[],
  students: DetailedCsvStudent[],
): string[][] {
  const header = [
    ...STUDENT_HEADERS,
    ...questions.flatMap((question) => questionColumnHeaders(question)),
  ];

  const totalMax = questions.reduce((total, question) => total + question.maxScore, 0);
  const maxRow = [
    'Max Score',
    '',
    '',
    '',
    formatNumber(totalMax),
    ...questions.flatMap((question) => questionMaxCells(question)),
  ];

  const studentRows = students.map((student) => [
    student.lastName,
    student.firstName,
    student.email,
    student.instituteId,
    formatNumber(studentTotalScore(student, questions)),
    ...questions.flatMap((question) =>
      questionStudentCells(student.questionStats[question.index], question),
    ),
  ]);

  return [header, maxRow, ...studentRows];
}

export function buildDetailedScoresFilename(componentTitle: string): string {
  const safe = componentTitle
    .replace(/[/\\:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return `${safe || 'assignment'} - detailed scores.csv`;
}

export function exportDetailedScoresCsv(
  componentTitle: string,
  questions: DetailedCsvQuestion[],
  students: DetailedCsvStudent[],
): void {
  const csv = toCsv(buildDetailedScoresRows(questions, students));
  downloadTextFile(buildDetailedScoresFilename(componentTitle), csv);
}
